/**
 * Trimming the server's restated choices out of an interviewer turn.
 *
 * A structured question carries its options *as text* because the plain-text
 * answer contract is the same on every channel (Slack, WhatsApp, voice — none
 * of which have a widget to draw). Here the chips/select under the bubble lay
 * out the very same options, so that block is pure duplication in the thread:
 * cut it where it begins.
 *
 * The markers are the opening words the server writes in
 * `domain/structured.format_*_prompt` — matched literally, since the block is
 * server-generated and untranslated. `tests/test_chat_choices_suffix.py`
 * guards this table (and chat.js's identical one) against prompt rewording.
 *
 * A re-prompt is *only* that block, so it stands on the widget alone with a
 * line of its own rather than collapsing to an empty bubble.
 */
import { consentSuffixIndex, translate } from "./i18n";
import type { UISpec, UISpecKind } from "./types";

export const CHOICES_SUFFIX: Record<string, { marker: string; reprompt: string }> = {
  options: {
    marker: "Please choose one of the following",
    reprompt: "Let's try that again — choose one of the options below.",
  },
  multi_select: {
    marker: "Please choose all that apply",
    reprompt: "Let's try that again — choose from the options below.",
  },
  scale: {
    marker: "Please answer on a scale from",
    reprompt: "Let's try that again — pick a point on the scale below.",
  },
  ranking: {
    marker: "Please rank ALL of the following",
    reprompt: "Let's try that again — put every item in order below.",
  },
  card_sort: {
    marker: "Please sort each of these items into one of the groups.",
    reprompt: "Let's try that again — put every item into one of the groups.",
  },
};

/** The consent gate spells its decision out in prose for channels with no
 *  buttons (`domain/structured.format_consent_suffix`); ours has buttons.
 *  This is the English form; the gate follows the interview's language, so
 *  the cut matches every language's (i18n.ts `consent_suffix`). */
export const CONSENT_SUFFIX_MARKER = "Please reply with Yes or No (or the number):";

/** Kinds where the widget itself draws the choices, so the text is redundant.
 *  `images`/`upload` restate nothing, and `none` is plain text. */
const RENDERS_CHOICES = new Set<UISpecKind>([
  "buttons",
  "select",
  "multi_select",
  "stepwise_ranking",
  "stepwise_cardsort",
]);

/**
 * What an interviewer turn shows, given the widget rendered under it: the
 * question, never the choices the widget is already showing. `spec` is the
 * question in play — pass null (or a spec that draws nothing) and the turn is
 * returned whole. `code` is the interview's language, for the re-prompt line
 * the widget writes itself (the choices markers are server English).
 */
export function bubbleText(raw: string, spec: UISpec | null, code = "en"): string {
  if (raw && spec && spec.qtype === "contact") {
    // The contact turn ends with how to answer in words (domain/contact.py
    // reply_instructions), for channels with no form; the form is right here.
    const cut = raw.lastIndexOf("\n\n");
    return cut > 0 ? raw.slice(0, cut) : raw;
  }
  if (!raw || !spec || !RENDERS_CHOICES.has(spec.kind)) return raw;
  const consentCut = spec.question_id === "consent" ? consentSuffixIndex(raw) : -1;
  if (consentCut > 0) return raw.slice(0, consentCut).trimEnd();
  const suffix = CHOICES_SUFFIX[spec.qtype];
  if (!suffix) return raw;
  const cut = raw.indexOf(suffix.marker);
  if (cut < 0) return raw;
  return cut === 0
    ? translate(code, `reprompt.${spec.qtype}`)
    : raw.slice(0, cut).trimEnd();
}
